import { A } from '@adornis/base/env-info.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { CampusLevumiLevel } from '../../campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { type LevumiMathCategory } from '../enums/CampusLevumiEnums.js';
import { ExerciseType } from '../enums/enums.js';
import { ChancenEventlog } from './ChancenEventlog.js';

@Entity()
export class LessonDiagnosticResult extends AdornisEntity {
  public static override _class = 'LessonDiagnosticResult';

  @Field(type => Boolean, {
    default: v => v ?? false,
  })
  public result!: boolean;

  @Field(type => String, {
    default: v => v ?? '',
  })
  public lessonID!: string;

  @Field(type => String, {
    default: v => v ?? '',
  })
  public contentID!: string;
}

@Entity()
export class LevumiAnswer extends AdornisEntity {
  public static override _class = 'LevumiAnswer';

  @Field(type => ID, {
    default: v => v ?? A.getGloballyUniqueID(),
  })
  public _id?: string;

  @Field(type => String)
  public question?: string;

  @Field(type => String)
  public studentAnswer?: string;

  @Field(type => String)
  public correctAnswer?: string;

  @Field(type => String)
  public category?: string;

  @Field(type => Int)
  public difficulty?: number;

  @Field(type => Boolean)
  public answerCorrect?: boolean;
}

@Entity()
export class LevumiResultInformation extends AdornisEntity {
  public static override _class = 'LevumiResultInformation';

  @Field(type => CampusLevumiLevel)
  public levumiLevel?: CampusLevumiLevel;

  @Field(type => String)
  public levumiMathCategory?: LevumiMathCategory;

  @Field(type => Int)
  public overallAnswered?: number;

  @Field(type => [LevumiAnswer])
  public questionsAndAnswers?: LevumiAnswer[];

  @Field(type => Int)
  public correctlyAnswered?: number;

  @Field(type => Float)
  public correctlyAnsweredPercent?: number;

  @Field(type => Float)
  public readingAccuracyInPercent?: number;

  @Field(type => Float)
  public readingSpeedInQuePerMin?: number;
}

@Entity()
export class LessonStartedResult extends AdornisEntity {
  static override _class = 'LessonStartedResult';

  @Field(type => String) lessonName?: string;

  @Field(type => Int) lessonCount?: number;
}

@Entity()
export class ContentResultEventlog extends ChancenEventlog {
  public static override _class = 'ContentResultEventlog';

  static override get allFields() {
    return selectionSet(() => ContentResultEventlog, 3);
  }

  constructor(doc: PartialEntityData<ContentResultEventlog>) {
    super(doc);
  }

  @Field(type => ID, {
    default: v => v ?? '',
  })
  public lessonID!: string;

  @Field(type => ID, {
    default: v => v ?? '',
  })
  public contentID!: string;

  @Field(type => String, {
    default: v => v ?? '',
  })
  public answer!: string;

  @Field(type => Int, {
    default: v => v ?? ExerciseType.FREITEXT,
  })
  public type!: ExerciseType;

  @Field(type => Boolean, {
    default: v => v ?? false,
  })
  public result!: boolean;

  @Field(type => LevumiResultInformation, {
    default: v => v ?? new LevumiResultInformation({}),
  })
  public levumiResultInformation!: LevumiResultInformation;
}

export const subscribeExerciseResultsForUser = registerSubscription({
  type: () => [ContentResultEventlog],
  operationName: 'getLessonUsageStatistics',
  params: [
    { type: () => String, name: 'userID' },
    { type: () => Int, name: 'type' },
  ],
  resolve: (userID: string, type?: ExerciseType) => (gqlFields: BaseQLSelectionSet<ContentResultEventlog>) => {
    const selector: Partial<ContentResultEventlog> = {
      _class: ContentResultEventlog._class,
      userID,
    };
    if (type !== undefined) {
      selector.type = type;
    }
    const handle = getCollectionHandle<ContentResultEventlog>(ContentResultEventlog._collectionName);
    return from(publishFilter(ContentResultEventlog._class)).pipe(
      switchMap(publishFilter => handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector)),
      catchError(e => {
        return of([] as ContentResultEventlog[]);
      }),
    );
  },
});

export const getLessonUsageStatistics = registerQuery({
  type: () => [LessonStartedResult],
  operationName: 'getLessonUsageStatistics',
  params: [{ type: () => Int, name: 'limit' }],
  resolve: (limit: number) => async (gqlFields: BaseQLSelectionSet<ContentResultEventlog>) => {
    const col = await getRawCollection(ContentResultEventlog._collectionName);
    const results = await col
      .aggregate([
        { $match: { _class: ContentResultEventlog._class } },
        { $group: { _id: { userID: '$userID', lessonID: '$lessonID' } } },
        { $group: { _id: '$_id.lessonID', num: { $sum: 1 } } },
        { $sort: { num: -1 } },
        ...(limit ? [{ $limit: limit }] : []),
        { $lookup: { from: 'campus-lessons', as: 'lesson', localField: '_id', foreignField: '_id' } },
        { $unwind: '$lesson' },
        { $project: { _id: 1, num: 1, lessonName: '$lesson.name' } },
      ])
      .toArray();

    return results.map(result => new LessonStartedResult({ lessonName: result.lessonName, lessonCount: result.num }));
  },
});

export const getLatestExerciseResultForUser = registerQuery({
  type: () => [ContentResultEventlog],
  operationName: 'getLatestExerciseResultForUser',
  params: [
    { type: () => String, name: 'userID' },
    { type: () => Int, name: 'type' },
  ],
  resolve: (userID: string, type?: ExerciseType) => async (gqlFields: BaseQLSelectionSet<ContentResultEventlog>) => {
    const selector: Partial<ContentResultEventlog> = {
      _class: ContentResultEventlog._class,
      userID,
    };
    if (type !== undefined) {
      selector.type = type;
    }
    const eventlogCol = await getCollection(ContentResultEventlog._class);
    const latestLogArr = (await eventlogCol
      .find(selector, {
        sort: { createdAt: -1 },
        limit: 1,
      })
      .toArray()) as ContentResultEventlog[];
    return latestLogArr;
  },
});
